import React, { useRef, useEffect, useState } from 'react';
import { 
  DocumentArrowUpIcon, 
  ClipboardDocumentIcon, 
  FolderOpenIcon,
  DocumentIcon,
  PhotoIcon,
  DocumentTextIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  ArchiveBoxIcon,
  XMarkIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import usePasteUpload from '../hooks/usePasteUpload';

const PasteUploadArea = ({
  onFileSelect,
  onFilePaste,
  accept,
  maxSize = 5 * 1024 * 1024,
  multiple = false,
  disabled = false,
  className = '',
  children,
  dragDropText = 'Drag & drop files here or press Ctrl+V',
  pasteText = 'Paste',
  selectText = 'Select',
  placeholder = 'Drop files here or press Ctrl+V to paste',
  uploadedUrl = null
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isPasteActive, setIsPasteActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  // Parse accept types for validation
  const acceptTypes = accept && typeof accept === 'string' ? accept.split(',').map(type => type.trim()) : null;

  // Helper function to check if file type is allowed
  const isFileTypeAllowed = (fileType, fileName) => {
    if (!acceptTypes || acceptTypes.includes('*/*')) return true;
    
    // Map of MIME types to extensions
    const mimeToExt = {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/jpg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'image/svg+xml': ['.svg'],
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    };
    
    // Check if MIME type is allowed (for pasted/dragged files)
    if (mimeToExt[fileType]) {
      const extensions = mimeToExt[fileType];
      if (extensions.some(ext => acceptTypes.includes(ext))) {
        return true;
      }
    }
    
    // Check if file extension matches
    if (fileName) {
      const fileExt = '.' + fileName.split('.').pop().toLowerCase();
      if (acceptTypes.includes(fileExt)) {
        return true;
      }
    }
    
    // Check if MIME type matches directly (for image/*)
    if (acceptTypes.includes('image/*') && fileType.startsWith('image/')) {
      return true;
    }
    
    // Check exact MIME type match
    if (acceptTypes.includes(fileType)) {
      return true;
    }
    
    return false;
  };

  // Validate files
  const validateFiles = (files) => {
    const validFiles = [];
    const errors = [];

    for (const file of files) {
      // Check file size
      if (file.size > maxSize) {
        errors.push(`${file.name} is too large. Maximum size is ${Math.round(maxSize / (1024 * 1024))}MB`);
        continue;
      }

      // Check file type
      if (!isFileTypeAllowed(file.type, file.name)) {
        const allowedTypesStr = acceptTypes ? acceptTypes.join(', ') : 'any';
        errors.push(`${file.name} type not allowed. Allowed types: ${allowedTypesStr}`);
        continue;
      }

      validFiles.push(file);
    }

    // Show errors if any
    if (errors.length > 0) {
      errors.forEach(error => {
        console.warn(error);
        // You can also show a toast notification here if desired
        // toast.error(error);
      });
    }

    return validFiles;
  };

  // Get file type icon
  const getFileTypeIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return PhotoIcon;
    } else if (file.type === 'application/pdf') {
      return DocumentTextIcon;
    } else if (file.type.includes('word') || file.type.includes('document')) {
      return DocumentTextIcon;
    } else if (file.type.includes('excel') || file.type.includes('spreadsheet')) {
      return TableCellsIcon;
    } else if (file.type.includes('powerpoint') || file.type.includes('presentation')) {
      return PresentationChartBarIcon;
    } else if (file.type.includes('zip') || file.type.includes('rar') || file.type.includes('archive')) {
      return ArchiveBoxIcon;
    } else {
      return DocumentIcon;
    }
  };

  // Check if URL is an image
  const isImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const extension = url.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension);
  };

  // Get file type icon from URL
  const getFileTypeIconFromUrl = (url) => {
    if (!url || typeof url !== 'string') return DocumentIcon;
    const extension = url.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
      return PhotoIcon;
    } else if (extension === 'pdf') {
      return DocumentTextIcon;
    } else if (['doc', 'docx'].includes(extension)) {
      return DocumentTextIcon;
    } else if (['xls', 'xlsx'].includes(extension)) {
      return TableCellsIcon;
    } else if (['ppt', 'pptx'].includes(extension)) {
      return PresentationChartBarIcon;
    } else if (['zip', 'rar', '7z'].includes(extension)) {
      return ArchiveBoxIcon;
    } else {
      return DocumentIcon;
    }
  };

  // Handle image preview
  const handleImagePreview = (imageSrc, fileName) => {
    setPreviewImage({ src: imageSrc, name: fileName });
    setShowPreviewModal(true);
  };

  // Close preview modal
  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewImage(null);
  };

  const handleFilePaste = (files) => {
    if (disabled) return;
    
    setSelectedFiles(multiple ? files : [files[0]]);
    
    if (multiple) {
      onFilePaste?.(files);
      onFileSelect?.(files);
    } else {
      onFilePaste?.(files[0]);
      onFileSelect?.(files[0]);
    }
  };

  const { setupPasteListener } = usePasteUpload(handleFilePaste, acceptTypes, maxSize);

  useEffect(() => {
    if (containerRef.current) {
      const cleanup = setupPasteListener(containerRef.current);
      return cleanup;
    }
  }, [setupPasteListener]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles(multiple ? validFiles : [validFiles[0]]);
        if (multiple) {
          onFileSelect?.(validFiles);
        } else {
          onFileSelect?.(validFiles[0]);
        }
      }
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles(multiple ? validFiles : [validFiles[0]]);
        
        if (multiple) {
          onFileSelect?.(validFiles);
        } else {
          onFileSelect?.(validFiles[0]);
        }
      }
    }
  };

  const handleSelectClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handlePasteClick = () => {
    if (disabled) return;
    
    if (containerRef.current) {
      containerRef.current.focus();
      setIsPasteActive(true);
      setTimeout(() => setIsPasteActive(false), 2000);
    }
  };

  const handleRemoveFile = (fileToRemove) => {
    const newFiles = selectedFiles.filter(file => file !== fileToRemove);
    setSelectedFiles(newFiles);
    
    if (multiple) {
      onFileSelect?.(newFiles);
    } else {
      onFileSelect?.(newFiles[0] || null);
    }
  };

  // File preview component for 64px height
  const FilePreview = ({ file, onRemove }) => {
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
      if (file && file.type && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          setImagePreview(null);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
    }, [file]);

    if (imagePreview && file.type.startsWith('image/')) {
      // Image preview
      return (
        <div className="relative h-14 w-14 flex-shrink-0">
          <img
            src={imagePreview}
            alt={file.name}
            className="h-14 w-14 object-cover rounded-md cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => handleImagePreview(imagePreview, file.name)}
            onError={(e) => {
              console.error('Image failed to load:', file.name);
              setImagePreview(null);
            }}
          />
          <button
            type="button"
            onClick={() => onRemove(file)}
            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
            title="Remove file"
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        </div>
      );
    }
    // File icon preview
    const FileTypeIcon = getFileTypeIcon(file);
    return (
      <div className="relative h-14 w-14 flex-shrink-0 bg-gray-100 rounded-md flex items-center justify-center">
        <FileTypeIcon className="h-8 w-8 text-gray-500" />
        <button
          type="button"
          onClick={() => onRemove(file)}
          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
          title="Remove file"
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      </div>
    );
  };

  // URL preview component
  const UrlPreview = ({ url, onRemove }) => {
    // Validate URL
    if (!url || typeof url !== 'string') {
      return null;
    }

    if (isImageUrl(url)) {
      // Image URL preview
      return (
        <div className="relative h-14 w-14 flex-shrink-0">
          <img
            src={url}
            alt="Uploaded file"
            className="h-14 w-14 object-cover rounded-md cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => handleImagePreview(url, 'Uploaded Image')}
            onError={(e) => {
              // Fallback to icon if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="relative h-14 w-14 flex-shrink-0 bg-gray-100 rounded-md flex items-center justify-center" style={{ display: 'none' }}>
            <PhotoIcon className="h-8 w-8 text-gray-500" />
          </div>
          {/* <button
            type="button"
            onClick={() => onRemove(url)}
            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
            title="Remove file"
          >
            <XMarkIcon className="h-3 w-3" />
          </button> */}
        </div>
      );
    } else {
      // File icon preview for URL
      const FileTypeIcon = getFileTypeIconFromUrl(url);
      return (
        <div className="relative h-14 w-14 flex-shrink-0 bg-gray-100 rounded-md flex items-center justify-center">
          <FileTypeIcon className="h-8 w-8 text-gray-500" />
          <button
            type="button"
            onClick={() => onRemove(url)}
            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
            title="Remove file"
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        </div>
      );
    }
  };

  const handleDownload = () => {
    if (!uploadedUrl) return;
    
    try {
      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = uploadedUrl;
      
      // Extract filename from URL or use default
      const urlParts = uploadedUrl.split('/');
      const fileName = urlParts[urlParts.length - 1] || 'download';
      
      // Set download attributes
      link.download = fileName;
      link.target = '_blank';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(uploadedUrl, '_blank');
    }
  };

  // Determine what to show
  const hasFiles = selectedFiles.length > 0 || (uploadedUrl && typeof uploadedUrl === 'string' && uploadedUrl.trim() !== '');

  return (
    <>
      <div
        ref={containerRef}
        className={`
          relative border-2 border-dashed rounded-lg h-16 transition-all duration-200
          ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${isPasteActive ? 'border-green-400 bg-green-50' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${className}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        tabIndex={0}
        role="button"
        aria-label="File upload area"
      >
        <input
          ref={fileInputRef}
          type="file"
          className="sr-only"
          onChange={handleFileInputChange}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
        />
        
        <div className="flex items-center justify-between h-full px-4">
          {/* Left side - File previews or no-file icon */}
          <div className="flex items-center space-x-3 flex-1 min-w-0" style={{ marginRight: '20px' }}>
            {hasFiles ? (
              // Show file previews
              <>
                {selectedFiles.map((file, index) => (
                  <FilePreview
                    key={`${file.name}-${index}`}
                    file={file}
                    onRemove={handleRemoveFile}
                  />
                ))}
                {uploadedUrl && typeof uploadedUrl === 'string' && uploadedUrl.trim() !== '' && (
                  <UrlPreview
                    key="uploaded-url"
                    url={uploadedUrl}
                    onRemove={() => onFileSelect?.(null)}
                  />
                )}
              </>
            ) : (
              // Show no-file icon
              <div className="flex items-center space-x-2">
                <DocumentArrowUpIcon className="h-6 w-6 text-gray-400" />
                <span className="text-sm text-gray-500 truncate">{placeholder}</span>
              </div>
            )}
          </div>
          
          {/* Right side - Select button (icon only) */}
          <div className="flex items-center space-x-2">
            {/* <button
              type="button"
              onClick={handlePasteClick}
              disabled={disabled}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title={pasteText}
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
            </button> */}
            
            <button
              type="button"
              onClick={handleSelectClick}
              disabled={disabled}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title={selectText}
            >
              <FolderOpenIcon className="h-4 w-4" />
            </button>
            
            {uploadedUrl && (
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Download"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        
        {/* Paste indicator */}
        {isPasteActive && (
          <div className="absolute inset-0 bg-green-100 bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="bg-green-500 text-white px-3 py-1 rounded-md shadow-lg text-sm">
              <ClipboardDocumentIcon className="h-4 w-4 inline mr-1" />
              Ready for paste! Press Ctrl+V
            </div>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {showPreviewModal && previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-4xl mx-4">
            {/* Close button */}
            <button
              onClick={closePreviewModal}
              className="absolute -top-4 -right-4 bg-white text-gray-800 rounded-full p-2 hover:bg-gray-100 transition-colors z-10"
              title="Close preview"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            
            {/* Image */}
            <img
              src={previewImage.src}
              alt={previewImage.name}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                console.error('Image failed to load in preview:', previewImage.name);
                closePreviewModal();
              }}
            />
            
            {/* Image name */}
            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded-md">
              <p className="text-sm truncate">{previewImage.name}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PasteUploadArea; 